import { createFileRoute } from "@tanstack/react-router";
import type { JSX } from "react";
import { createElement, useEffect, useMemo, useRef } from "react";
import { AppConfig } from "@/common/appConfig";
import { underV3 } from "@/components/ui/v3/flagged";
import { env } from "@/env";
import { cn } from "@/lib/utils";
import { StripeCheckout } from "@/pages/Stripe/StripeCheckout";

const clientSecretFromUrl = (): string =>
  new URL(globalThis.location.href).searchParams.get("clientSecret") || "";

// v3.2 app: the checkout page with its loading, error and no-session states.
const StripeCheckoutPage = (): JSX.Element => {
  const clientSecret = useMemo(clientSecretFromUrl, []);
  return createElement(StripeCheckout, {
    config: {
      tableId: env.VITE_STRIPE_TABLE_ID,
      publishableKey: env.VITE_STRIPE_KEY,
      clientSecret,
    },
  });
};

const StripePage = (): JSX.Element => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  const clientSecret = useMemo(clientSecretFromUrl, []);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://js.stripe.com/v3/pricing-table.js";
    script.async = true;
    document.head.appendChild(script);

    return (): void => {
      script.remove();
    };
  }, []);

  useEffect(() => {
    const tableId = env.VITE_STRIPE_TABLE_ID;
    const publishableKey = env.VITE_STRIPE_KEY;
    const container = containerRef.current;

    if (!container) return;

    if (!tableId || !publishableKey || !clientSecret) {
      container.innerHTML =
        '<div style="padding:12px;border:1px solid #fca5a5;background:#fef2f2;color:#991b1b;border-radius:8px;">Missing Stripe configuration or clientSecret.</div>';
      return;
    }

    container.innerHTML = `
      <stripe-pricing-table
        pricing-table-id="${tableId}"
        publishable-key="${publishableKey}"
        customer-session-client-secret="${clientSecret}"
      ></stripe-pricing-table>
    `;
  }, [clientSecret]);

  return createElement(
    "div",
    {
      className: cn(
        "min-h-screen w-full bg-background p-4 md:p-8",
        // v3.2 app: the v3.2 canvas and type around Stripe's table.
        underV3("v3-remap bg-v3-canvas font-v3 text-v3-text"),
      ),
    },
    createElement(
      "div",
      { className: "mx-auto max-w-4xl" },
      createElement(
        "h1",
        {
          className: cn(
            "mb-4 text-xl font-semibold",
            underV3("font-v3-display text-[22px] tracking-[-0.01em]"),
          ),
        },
        "ROSC Subscription",
      ),
      createElement("div", { ref: containerRef }),
    ),
  );
};

export const Route = createFileRoute("/stripe")({
  component: (): JSX.Element =>
    AppConfig.enableAppRedesign
      ? createElement(StripeCheckoutPage)
      : createElement(StripePage),
});
